// Begrenzter localStorage-Cache (Byte-Budget + LRU/TTL-Eviction).
//
// Problem (2026-06-24): Mehrere regenerierbare Preis-Caches wuchsen UNBEGRENZT und
// sprengten das harte localStorage-Limit (~10 MB in Chrome):
//   • red_pxhist_<coin>_<cur>_<days>        (portfolioHistory.js, ~200 KB/Coin bei 90d)
//   • redapp_cg_hist_v1__<coin>_<days>_<cur> (coingecko.js, market_chart)
//   • redapp_cg_hist_day_v1__<coin>_<date>_<cur> (coingecko.js, Tagespreise)
// Firestore (persistentLocalCache + Multi-Tab) schreibt seinen Sync-State EBENFALLS in
// localStorage ("firestore_targets_…"). War der Speicher durch die Preis-Caches voll,
// scheiterte Firestores setItem mit QuotaExceededError → "INTERNAL UNHANDLED ERROR" →
// kompletter App-Crash (ErrorBoundary "App Crash — bitte Screenshot schicken").
//
// Lösung: Alle regenerierbaren Preis-Caches teilen ein hartes Byte-Budget. Sie werden
// nur noch über safeLocalSet() geschrieben; bei Überschreitung / QuotaExceededError
// werden die ÄLTESTEN Einträge (nach gespeichertem fetchedAt/ts) verdrängt, sodass
// IMMER genug Headroom für Firestore + App-State bleibt. enforceCacheBudget() läuft
// zusätzlich beim Boot (firebase.js, VOR der Firestore-Init), damit bereits vollgelaufene
// Installationen sich selbst heilen, ohne dass der User den Cache manuell leeren muss.

// Präfixe der regenerierbaren Caches, die dem gemeinsamen Budget unterliegen.
const CACHE_PREFIXES = [
  'red_pxhist_',
  'redapp_cg_hist_v1__',
  'redapp_cg_hist_day_v1__',
];

// Gesamt-Budget für ALLE obigen Caches. Bewusst konservativ: das harte localStorage-
// Limit liegt browserabhängig bei ~5–10 MB; 4 MB für die Preis-Caches lassen reichlich
// Platz für Firestore (firestore_targets_…), red_market_cache und sonstigen App-State.
const CACHE_BUDGET_BYTES = 4 * 1024 * 1024;

function hasLS() {
  return typeof localStorage !== 'undefined' && localStorage != null;
}

function isCacheKey(k) {
  return !!k && CACHE_PREFIXES.some((p) => k.startsWith(p));
}

// UTF-16: jedes Zeichen ~2 Byte. Grobe, aber konsistente Abschätzung.
function entryBytes(k, v) {
  return (k.length + (v ? v.length : 0)) * 2;
}

// Liest den Zeitstempel (fetchedAt/ts) BILLIG aus dem Anfang des serialisierten Werts,
// ohne den (ggf. 200 KB großen) JSON komplett zu parsen. Alle Cache-Writer legen den
// Zeitstempel am Anfang ab. Fehlt er → 0 ⇒ gilt als ältester Eintrag (zuerst verdrängt).
function parseTs(v) {
  if (!v) return 0;
  const head = v.length > 160 ? v.slice(0, 160) : v;
  const m = /"(?:fetchedAt|ts)":\s*(\d+)/.exec(head);
  return m ? Number(m[1]) : 0;
}

function isQuotaError(e) {
  return !!e && (
    e.name === 'QuotaExceededError' ||
    e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    e.code === 22 || e.code === 1014
  );
}

// Sammelt alle Cache-Einträge mit Größe + Zeitstempel und die Gesamtgröße.
function collectCacheEntries() {
  const entries = [];
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!isCacheKey(k)) continue;
    const v = localStorage.getItem(k);
    const bytes = entryBytes(k, v);
    total += bytes;
    entries.push({ k, ts: parseTs(v), bytes });
  }
  return { entries, total };
}

// Verdrängt die ältesten Cache-Einträge, bis mind. `bytesToFree` frei wurden.
function evictOldest(bytesToFree) {
  const { entries } = collectCacheEntries();
  entries.sort((a, b) => a.ts - b.ts); // ältester zuerst
  let freed = 0;
  for (const e of entries) {
    try { localStorage.removeItem(e.k); } catch { /* ignore */ }
    freed += e.bytes;
    if (freed >= bytesToFree) break;
  }
  return freed;
}

// Erzwingt das Gesamt-Budget aller Caches (proaktiv nach jedem Write + beim Boot).
export function enforceCacheBudget(budget = CACHE_BUDGET_BYTES) {
  if (!hasLS()) return;
  try {
    const { entries, total } = collectCacheEntries();
    if (total <= budget) return;
    entries.sort((a, b) => a.ts - b.ts);
    let over = total - budget;
    for (const e of entries) {
      if (over <= 0) break;
      try { localStorage.removeItem(e.k); } catch { /* ignore */ }
      over -= e.bytes;
    }
  } catch { /* localStorage nicht verfügbar */ }
}

// Schreibt einen Cache-Wert robust:
//   1) normaler setItem-Versuch
//   2) bei QuotaExceededError: ältere Cache-Einträge verdrängen, EINMAL erneut versuchen
//   3) nach Erfolg das Budget erzwingen (hält die Summe dauerhaft unter dem Limit)
// Gibt true bei Erfolg zurück. Wirft NIE — der Cache ist reine Optimierung.
export function safeLocalSet(key, value) {
  if (!hasLS()) return false;
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    if (!isQuotaError(e)) return false;
    // Platz schaffen: das Doppelte des neuen Werts, mind. 1 MB.
    evictOldest(Math.max(entryBytes(key, value) * 2, 1024 * 1024));
    try {
      localStorage.setItem(key, value);
    } catch {
      return false;
    }
  }
  enforceCacheBudget();
  return true;
}
